import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import ChangeUserPassword from "./../../pages/ChangeUserPassword/ChangeUserPassword";
import SignOut from "./../auth/SignOut";
import ErrorPage from "../../pages/Error/ErrorPage";
import UserDetailsNotAllowed from "./../UserDetailsNotAllowed/UserDetailsNotAllowed";
import TalaeraDefaultBackground from "./../TalaeraDefaultBackground/TalaeraDefaultBackground";
import PrivateRoute from "./PrivateRoute";
import {
  hasPrimaryInstructor,
  isGroupUser,
  isOneOnOneUser,
  isSelfStudyUser,
  isUserCompanyManager,
  isUserStudent,
  isUserTeacher,
  isUserTeamManager,
} from "services/authUtil";
import { isFavoriteInstructor, isFavoriteStudent } from "services/favorites";
import TalaeraRoutes from "./TalaeraRoutes";
import { ErrorBoundary } from "../ErrorBoundary";
import UnauthRoutes from "./UnauthRoutes";
import useUser from "hooks/useUser";

const AsyncMyDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "MyDashboardPage" */
      "../../pages/MyDashboard/MyDashboardPage"
    )
);
const AsyncMyProgress = React.lazy(
  () =>
    import(
      /* webpackChunkName: "MyProgress" */
      "./../../pages/MyProgress"
    )
);
const AsyncLessonFeedback = React.lazy(
  () =>
    import(
      /* webpackChunkName: "LessonFeedbackPage" */
      "./../../pages/LessonFeedback/LessonFeedbackPage"
    )
);
const AsyncPostSessionFeedback = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PostSessionFeedbackPage" */
      "./../../pages/PostSessionFeedback/PostSessionFeedbackPage"
    )
);
const AsyncSearch = React.lazy(
  () =>
    import(
      /* webpackChunkName: "SearchPage" */
      "./../../pages/Search/SearchPage"
    )
);
const AsyncStudentSettings = React.lazy(
  () =>
    import(
      /* webpackChunkName: "StudentSettingsPage" */
      "../../pages/Settings/StudentSettingsPage"
    )
);
const AsyncTeacherSettings = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TeacherSettingsPage" */
      "./../../pages/Settings/TeacherSettingsPage"
    )
);
const AsyncStudents = React.lazy(
  () =>
    import(
      /* webpackChunkName: "StudentsPage" */
      "./../../pages/Students/StudentsPage"
    )
);
const AsyncStudentDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "StudentDetailsPage" */
      "./../../pages/StudentDetails/StudentDetailsPage"
    )
);
const AsyncTeacherDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TeacherDetailsPage" */
      "./../../pages/TeacherDetails/TeacherDetailsPage"
    )
);
const AsyncConversation = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ConversationPage" */
      "./../../pages/Conversation/ConversationPage"
    )
);
const AsyncTeamDashboard = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TeamDashboardPage" */
      "./../../pages/team/TeamDashboard/TeamDashboardPage"
    )
);
const AsyncCompanyReportsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ReportsPage" */
      "./../../pages/team/ReportsPage/index"
    )
);
const AsyncTeamsPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TeamsPage" */
      "../../pages/team/CompanyDashboard/TeamsPage"
    )
);
const AsyncTeamMember = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TeamMemberPage" */
      "./../../pages/team/TeamMember/TeamMemberPage"
    )
);
const AsyncLibrary = React.lazy(
  () =>
    import(
      /* webpackChunkName: "LibraryPage" */
      "./../../pages/Library/LibraryPage"
    )
);
const AsyncLibraryRoot = React.lazy(
  () =>
    import(
      /* webpackChunkName: "LibraryRootPage" */
      "../../pages/Library/LibraryRootPage"
    )
);
const AsyncExercises = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExercisesPage" */
      "./../../pages/Exercises/ExercisesPage"
    )
);
const AsyncExerciseDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExerciseDetailsPage" */
      "./../../pages/ExerciseDetails/ExerciseDetailsPage"
    )
);
const AsyncExerciseAssignmentDetails = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExerciseAssignmentDetailsPage" */
      "../../pages/ExerciseAssignmentDetails/ExerciseAssignmentDetailsPage"
    )
);
const AsyncTagSearch = React.lazy(
  () =>
    import(
      /* webpackChunkName: "TagSearchPage" */
      "./../../pages/TagSearch/TagSearchPage"
    )
);
const AsyncExerciseCreator = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExerciseCreatorPage" */
      "./../../pages/ExerciseCreator/ExerciseCreatorPage"
    )
);
const AsyncExerciseEditor = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ExerciseEditorPage" */
      "./../../pages/ExerciseEditor/ExerciseEditorPage"
    )
);
const AsyncMyTeacherExerciseList = React.lazy(
  () =>
    import(
      /* webpackChunkName: "MyTeacherExerciseListPage" */
      "./../../pages/Exercises/MyTeacherExerciseListPage"
    )
);
const AsyncCoursePage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "CoursePage" */
      "../../pages/Course/CoursePage"
    )
);
const AsyncLevelTestPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "LevelTest" */
      "../../pages/LevelTest"
    )
);
const AsyncPaymentRedirectPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "PaymentRedirect" */
      "../../pages/PaymentRedirect"
    )
);
const AsyncSpeakingclubPage = React.lazy(
  () =>
    import(
      /* webpackChunkName: "SpeakingClubPage" */
      "../../pages/SpeakingClubPage"
    )
);

const RedirectToRoot = () => {
  return <Navigate to={TalaeraRoutes.ROOT.path} replace />;
};

interface UserRoutesProps {
  location: {
    pathname: string;
  };
  smallWindow: boolean;
  showToast: (message: string) => void;
  openSidebar?: (selection: any, mode: any) => void;
  refreshHeader?: () => void;
  previousLocation?: string;
}

export default function UserRoutes(props: UserRoutesProps) {
  const user = useUser();
  const isTeacher = isUserTeacher(user);
  const isStudent = isUserStudent(user);
  const isTeamManager = isUserTeamManager(user);
  const isCompanyManager = isUserCompanyManager(user);
  const showStudentRoutes =
    (isStudent && !isTeamManager) || (isTeamManager && hasPrimaryInstructor(user));
  const isLibraryOnly = isSelfStudyUser(user);
  const isGroup = isGroupUser(user);
  const isOneOnOneStudent = isOneOnOneUser(user);

  return (
    <ErrorBoundary pathname={props.location?.pathname}>
      <Routes key={props.location.pathname}>
        {isTeamManager && !showStudentRoutes && (
          <Route
            path={TalaeraRoutes.ROOT.path}
            element={<Navigate to={TalaeraRoutes.TEAM_DASHBOARD.path} replace />}
          />
        )}

        {isGroup && (
          <Route
            path={TalaeraRoutes.ROOT.path}
            element={<Navigate to={TalaeraRoutes.LIBRARY_ROOT.path} replace />}
          />
        )}

        {!isGroup && (isTeacher || isLibraryOnly || showStudentRoutes) && (
          <Route
            path={TalaeraRoutes.ROOT.path}
            element={
              <PrivateRoute
                component={AsyncMyDashboard}
                permissions={["student", "instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {!(isLibraryOnly || isGroup) && (isTeacher || showStudentRoutes) && (
          <Route
            path={TalaeraRoutes.MY_DASHBOARD.path}
            element={
              <PrivateRoute
                component={AsyncMyDashboard}
                permissions={["student", "instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {(isLibraryOnly || isGroup || isOneOnOneStudent) && (
          <Route
            path={TalaeraRoutes.MY_PROGRESS.path}
            element={
              <PrivateRoute
                component={AsyncMyProgress}
                permissions={["student"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {isTeacher && (
          <Route
            path={TalaeraRoutes.EXERCISES.path}
            element={
              <PrivateRoute
                component={AsyncExercises}
                permissions={["instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {isTeacher && (
          <Route
            path={TalaeraRoutes.EXERCISES_BY_CREATOR.path}
            element={
              <PrivateRoute
                component={AsyncMyTeacherExerciseList}
                permissions={["instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {isTeacher && (
          <Route
            path={TalaeraRoutes.EXERCISE_CREATE.path}
            element={
              <PrivateRoute
                component={AsyncExerciseCreator}
                permissions={["instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {isTeacher && (
          <Route
            path={TalaeraRoutes.EXERCISE_EDIT.path}
            element={
              <PrivateRoute
                component={AsyncExerciseEditor}
                permissions={["instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {(isTeacher || showStudentRoutes) && (
          <Route
            path={TalaeraRoutes.EXERCISE_DETAILS.path}
            element={
              <PrivateRoute
                component={AsyncExerciseDetails}
                permissions={["student", "instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
                previousLocation={props.previousLocation}
              />
            }
          />
        )}

        {(isTeacher || showStudentRoutes) && (
          <Route
            path={TalaeraRoutes.EXERCISE_ASSIGNMENT_DETAILS.path}
            element={
              <PrivateRoute
                component={AsyncExerciseAssignmentDetails}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
                permissions={["student", "instructor"]}
                previousLocation={props.previousLocation}
              />
            }
          />
        )}

        {isStudent && (
          <Route
            path={TalaeraRoutes.SPEAKING_CLUB.path}
            element={
              <PrivateRoute
                component={AsyncSpeakingclubPage}
                permissions={["student"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {(isTeacher || showStudentRoutes) && (
          <Route
            path={TalaeraRoutes.SESSION_FEEDBACK.path}
            element={
              <PrivateRoute
                component={AsyncLessonFeedback}
                smallWindow={props.smallWindow}
                openSidebar={props.openSidebar}
                showToast={props.showToast}
                permissions={["student", "instructor"]}
                mode="view"
              />
            }
          />
        )}

        {(isTeacher || showStudentRoutes) && (
          <Route
            path={TalaeraRoutes.TAG_SEARCH.path}
            element={
              <PrivateRoute
                component={AsyncTagSearch}
                smallWindow={props.smallWindow}
                permissions={["student", "instructor"]}
              />
            }
          />
        )}

        {isTeacher && (
          <Route
            path={TalaeraRoutes.SESSION_FEEDBACK_EDIT.path}
            element={
              <PrivateRoute
                component={AsyncLessonFeedback}
                smallWindow={props.smallWindow}
                openSidebar={props.openSidebar}
                showToast={props.showToast}
                refreshPageHeader={props.refreshHeader}
                permissions={["instructor"]}
                mode="edit"
              />
            }
          />
        )}

        {isTeacher && (
          <Route
            path={TalaeraRoutes.SEARCH.path}
            element={
              <PrivateRoute
                component={AsyncSearch}
                permissions={["instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {isTeacher && (
          <Route
            path={TalaeraRoutes.SETTINGS.path}
            element={
              <PrivateRoute
                component={AsyncTeacherSettings}
                permissions={["instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {isStudent && (
          <Route
            path={TalaeraRoutes.SETTINGS.path}
            element={
              <PrivateRoute
                component={AsyncStudentSettings}
                permissions={["student"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {isTeacher && (
          <Route
            path={TalaeraRoutes.STUDENTS.path}
            element={
              <PrivateRoute
                component={AsyncStudents}
                permissions={["instructor"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {isTeacher && (
          <Route
            path={TalaeraRoutes.STUDENT_DETAILS.path}
            element={
              <PrivateRoute
                component={AsyncStudentDetails}
                permissions={(params: any, onSuccess: any, onError: any) =>
                  isFavoriteInstructor(params?.id)(onSuccess, onError)
                }
                loading={true}
                ifNotAllowed={<UserDetailsNotAllowed />}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
                refreshPageHeader={props.refreshHeader}
              />
            }
          />
        )}

        {showStudentRoutes && (
          <Route
            path={TalaeraRoutes.INSTRUCTOR_DETAILS.path}
            element={
              <PrivateRoute
                component={AsyncTeacherDetails}
                permissions={(params: any, onSuccess: any, onError: any) =>
                  isFavoriteStudent(params?.id)(onSuccess, onError)
                }
                loading={true}
                ifNotAllowed={<UserDetailsNotAllowed />}
                smallWindow={props.smallWindow}
              />
            }
          />
        )}

        {(isTeacher || showStudentRoutes) && (
          <Route
            path={TalaeraRoutes.SESSION_CONVERSATION.path}
            element={
              <PrivateRoute
                component={AsyncConversation}
                permissions={["instructor", "student"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {(isTeacher || showStudentRoutes) && (
          <Route
            path={TalaeraRoutes.POST_FEEDBACK.path}
            element={
              <PrivateRoute
                component={AsyncPostSessionFeedback}
                permissions={["instructor", "student"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {showStudentRoutes && (
          <Route
            path={TalaeraRoutes.SELF_SIGNUP_VERIFY_INDIVIDUAL.path}
            element={
              <PrivateRoute
                component={RedirectToRoot}
                permissions={["student", "team-manager"]}
                smallWindow={props.smallWindow}
              />
            }
          />
        )}

        <Route
          path={TalaeraRoutes.COURSE.path}
          element={
            <PrivateRoute
              component={AsyncCoursePage}
              permissions={["instructor", "student"]}
              showToast={props.showToast}
              smallWindow={props.smallWindow}
            />
          }
        />

        <Route
          path={TalaeraRoutes.COURSE_OBJECT.path}
          element={
            <PrivateRoute
              component={AsyncCoursePage}
              permissions={["instructor", "student"]}
              showToast={props.showToast}
              smallWindow={props.smallWindow}
            />
          }
        />

        <Route
          path={TalaeraRoutes.LIBRARY_ROOT.path}
          element={
            <PrivateRoute
              component={AsyncLibraryRoot}
              permissions={["instructor", "student"]}
              smallWindow={props.smallWindow}
            />
          }
        />

        <Route
          path={TalaeraRoutes.LIBRARY_OBJECT.path}
          element={
            <PrivateRoute
              component={AsyncLibrary}
              permissions={["instructor", "student"]}
              smallWindow={props.smallWindow}
              showToast={props.showToast}
            />
          }
        />

        {(isTeamManager || isCompanyManager) && (
          <Route
            path={TalaeraRoutes.TEAM_DASHBOARD_PARAMS.path}
            element={
              <PrivateRoute
                component={AsyncTeamDashboard}
                permissions={["team-manager", "company-manager"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        <Route
          path={TalaeraRoutes.LEVEL_TEST.path}
          element={
            <PrivateRoute
              component={AsyncLevelTestPage}
              permissions={["student"]}
              smallWindow={props.smallWindow}
              showToast={props.showToast}
            />
          }
        />

        {(isTeamManager || isCompanyManager) && (
          <Route
            path={TalaeraRoutes.TEAM_DASHBOARD.path}
            element={
              <PrivateRoute
                component={AsyncTeamsPage}
                permissions={["team-manager", "company-manager"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {isCompanyManager && (
          <Route
            path={TalaeraRoutes.COMPANY_REPORTS_PAGE.path}
            element={
              <PrivateRoute
                component={AsyncCompanyReportsPage}
                permissions={["company-manager"]}
                smallWindow={props.smallWindow}
                showToast={props.showToast}
              />
            }
          />
        )}

        {(isTeamManager || isCompanyManager) && (
          <Route
            path={TalaeraRoutes.TEAM_MEMBER_DETAILS.path}
            element={
              <PrivateRoute
                component={AsyncTeamMember}
                permissions={["team-manager", "company-manager"]}
                smallWindow={props.smallWindow}
              />
            }
          />
        )}

        <Route
          path={TalaeraRoutes.CHANGE_PASSWORD.path}
          element={<ChangeUserPassword smallWindow={props.smallWindow} />}
        />

        <Route
          path={TalaeraRoutes.SIGN_UP_WITH_CODE.path}
          element={
            <PrivateRoute
              permissions={["student"]}
              component={AsyncPaymentRedirectPage}
              smallWindow={props.smallWindow}
              showToast={props.showToast}
            />
          }
        />

        <Route
          path={TalaeraRoutes.SELF_SIGN_UP.path}
          element={
            <PrivateRoute
              permissions={["student"]}
              component={RedirectToRoot}
              smallWindow={props.smallWindow}
              showToast={props.showToast}
            />
          }
        />

        <Route path={TalaeraRoutes.ERROR.path} element={<ErrorPage />} />

        <Route
          path={TalaeraRoutes.SIGN_OUT.path}
          element={
            <TalaeraDefaultBackground>
              <SignOut />
            </TalaeraDefaultBackground>
          }
        />

        <Route path={"*"} element={<UnauthRoutes showWrongLinkIfAuthenticated />} />
      </Routes>
    </ErrorBoundary>
  );
}
